import { useState, useEffect, useCallback } from 'react';
import { Truck, AlertTriangle, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const badge = (s) => ({ 'Awaiting Confirmation': 'bg-gold/15 text-gold-dark', 'Confirmed': 'bg-forest/10 text-forest', 'Partially Confirmed': 'bg-forest/10 text-forest', 'Shortage Reported': 'bg-red-50 text-red-600', 'Picked Up': 'bg-dark/10 text-dark' }[s] || 'bg-dark/10 text-dark');
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [gen, setGen] = useState({ week: '', pickup_date: '', pickup_window: '2:00 PM - 4:00 PM' });
  const [cutoff, setCutoff] = useState({ cutoff_day: 'Wednesday', cutoff_time: '20:00' });

  const load = useCallback(() => {
    api.get('/admin/orders').then((r) => setOrders(r.data)).catch(() => {});
    api.get('/admin/orders/cutoff-settings').then((r) => setCutoff(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    const r = await api.post('/admin/orders/generate', gen);
    toast.success(r.data.message); load();
  };
  const pickup = async (id) => { await api.post(`/admin/orders/${id}/pickup`); toast.success('Marked picked up'); load(); };
  const saveCutoff = async () => {
    await api.put('/admin/orders/cutoff-settings', cutoff);
    toast.success('Weekly cutoff updated — farms will get their emailed order automatically after this time');
  };

  return (
    <div data-testid="admin-orders">
      <div className="bg-white rounded-2xl p-6 border border-forest/10 mb-6">
        <h2 className="font-playfair text-xl font-bold text-dark mb-4">Weekly Supplier-Order Cutoff</h2>
        <p className="font-manrope text-sm text-dark/60 mb-4">After this day/time each week, FarmBox automatically emails every approved farm their aggregated order (quantities only).</p>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div>
            <Label>Cutoff day</Label>
            <select value={cutoff.cutoff_day} onChange={(e) => setCutoff({ ...cutoff, cutoff_day: e.target.value })} className="bg-cream border border-forest/20 rounded-xl px-3 py-2 w-full" data-testid="cutoff-day-select">
              {WEEKDAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div><Label>Cutoff time (24h)</Label><Input type="time" className="bg-cream border-forest/20" value={cutoff.cutoff_time} onChange={(e) => setCutoff({ ...cutoff, cutoff_time: e.target.value })} data-testid="cutoff-time-input" /></div>
          <button onClick={saveCutoff} className="btn-secondary py-2.5 justify-center" data-testid="save-cutoff">Save Cutoff</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-forest/10 mb-6">
        <h2 className="font-playfair text-xl font-bold text-dark mb-4">Generate Weekly Farm Orders Now</h2>
        <p className="font-manrope text-sm text-dark/60 mb-4">Aggregates confirmed demand from farm availability into a purchase order for each approved farm, and emails each farm their order.</p>
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div><Label>Week</Label><Input type="date" className="bg-cream border-forest/20" value={gen.week} onChange={(e) => setGen({ ...gen, week: e.target.value })} /></div>
          <div><Label>Pickup date</Label><Input type="date" className="bg-cream border-forest/20" value={gen.pickup_date} onChange={(e) => setGen({ ...gen, pickup_date: e.target.value })} /></div>
          <div><Label>Pickup window</Label><Input className="bg-cream border-forest/20" value={gen.pickup_window} onChange={(e) => setGen({ ...gen, pickup_window: e.target.value })} /></div>
          <button onClick={generate} className="btn-primary py-2.5 justify-center" data-testid="generate-orders"><PlayCircle className="w-4 h-4 mr-2" />Generate</button>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-forest/10 p-12 text-center font-manrope text-dark/50">No orders yet. Generate weekly orders above.</div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.order_id} className="bg-white rounded-2xl border border-forest/10 p-6" data-testid={`admin-order-${o.order_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div><h3 className="font-playfair text-lg font-bold text-dark">{o.farm_name}</h3><p className="font-manrope text-xs text-dark/50">Week {o.week} · Pickup {o.pickup_date || 'TBD'} {o.pickup_window}</p></div>
                <div className="flex items-center gap-2"><span className={`px-3 py-1 rounded-full text-xs font-bold ${badge(o.status)}`}>{o.status}</span><span className="px-3 py-1 rounded-full text-xs font-bold bg-dark/5 text-dark">{o.pickup_status}</span></div>
              </div>
              <div className="font-manrope text-sm text-dark/70 mb-3">{o.items.map((it) => `${it.name} (${it.requested_quantity} ${it.unit})`).join(', ')} — <strong>${o.total.toFixed(2)}</strong></div>
              {o.shortages?.length > 0 && (
                <div className="bg-red-50 rounded-xl p-3 mb-3" data-testid={`shortage-alert-${o.order_id}`}>
                  {o.shortages.map((s, i) => <p key={i} className="flex items-center gap-2 font-manrope text-sm text-red-700"><AlertTriangle className="w-4 h-4" />Shortage: {s.product_id} — {s.quantity_available} available. {s.reason} {s.substitute && `Substitute: ${s.substitute}`}</p>)}
                </div>
              )}
              {o.pickup_status !== 'Picked Up' && <button onClick={() => pickup(o.order_id)} className="btn-secondary py-2 px-4 text-sm" data-testid={`admin-pickup-${o.order_id}`}><Truck className="w-4 h-4 mr-1" />Mark Picked Up</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
